import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  Button,
  IconButton,
  Collapse,
  ButtonGroup,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CommentIcon from "@mui/icons-material/Comment";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

interface HNStory {
  id: number;
  title: string;
  url?: string;
  score: number;
  by: string;
  time: number;
  descendants?: number;
  kids?: number[];
  text?: string;
  type?: string;
}

interface HNComment {
  id: number;
  by: string;
  text: string;
  time: number;
}

interface HackerNewsProps {
  count?: number;
  variant?: "single" | "grid";
}

type FeedType = "top" | "new" | "ask" | "show" | "saved";
type TimeFilter = "today" | "week" | "month";

const HackerNews: React.FC<HackerNewsProps> = ({
  count = 6,
  variant = "grid",
}) => {
  const navigate = useNavigate();

  // Try to restore previous state from sessionStorage
  const getInitialState = () => {
    const cached = sessionStorage.getItem("hn_state");
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch {
        return null;
      }
    }
    return null;
  };

  const initialState = getInitialState();

  const [stories, setStories] = useState<HNStory[]>(
    initialState?.stories || [],
  );
  const [loading, setLoading] = useState(!initialState);
  const [error, setError] = useState(false);
  const [feedType, setFeedType] = useState<FeedType>(
    initialState?.feedType || "top",
  );
  const [timeFilter, setTimeFilter] = useState<TimeFilter>(
    initialState?.timeFilter || "week",
  );
  const [isInitialLoad, setIsInitialLoad] = useState(!initialState);
  const [savedStories, setSavedStories] = useState<Set<number>>(
    () => new Set(JSON.parse(localStorage.getItem("hn_saved") || "[]")),
  );
  const [expandedComments, setExpandedComments] = useState<Set<number>>(
    new Set(),
  );
  const [comments, setComments] = useState<Map<number, HNComment>>(new Map());
  const [displayCount, setDisplayCount] = useState(
    initialState?.displayCount || count,
  );
  const [loadingMore, setLoadingMore] = useState(false);
  const [allStories, setAllStories] = useState<HNStory[]>(
    initialState?.allStories || [],
  );

  const getTimeFilterSeconds = useCallback(() => {
    const now = Date.now() / 1000;
    switch (timeFilter) {
      case "today":
        return now - 24 * 60 * 60;
      case "week":
        return now - 7 * 24 * 60 * 60;
      case "month":
        return now - 30 * 24 * 60 * 60;
      default:
        return now - 7 * 24 * 60 * 60;
    }
  }, [timeFilter]);

  const getFeedUrl = useCallback(() => {
    switch (feedType) {
      case "new":
        return "https://hacker-news.firebaseio.com/v0/newstories.json";
      case "ask":
        return "https://hacker-news.firebaseio.com/v0/askstories.json";
      case "show":
        return "https://hacker-news.firebaseio.com/v0/showstories.json";
      case "saved":
        return null; // Saved stories are fetched separately
      default:
        return "https://hacker-news.firebaseio.com/v0/topstories.json";
    }
  }, [feedType]);

  const fetchTopComment = async (storyId: number, kids?: number[]) => {
    if (!kids || kids.length === 0) return null;

    try {
      const commentResponse = await fetch(
        `https://hacker-news.firebaseio.com/v0/item/${kids[0]}.json`,
      );
      const comment: HNComment = await commentResponse.json();
      if (comment && comment.text) {
        setComments((prev) => new Map(prev).set(storyId, comment));
      }
    } catch (err) {
      console.error("Error fetching comment:", err);
    }
  };

  const fetchStories = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);

      // Handle saved stories separately
      if (feedType === "saved") {
        const savedIds = Array.from(savedStories);
        if (savedIds.length === 0) {
          setAllStories([]);
          setStories([]);
          setLoading(false);
          return;
        }

        const storyPromises = savedIds.map(async (id) => {
          const response = await fetch(
            `https://hacker-news.firebaseio.com/v0/item/${id}.json`,
          );
          return response.json();
        });

        const fetchedStories: HNStory[] = await Promise.all(storyPromises);
        const validStories = fetchedStories.filter((story) => story);
        validStories.sort((a, b) => b.score - a.score);

        setAllStories(validStories);
        const topStories = validStories.slice(0, count);
        setStories(topStories);

        // Fetch top comment for each story
        topStories.forEach((story) => {
          if (story.kids && story.kids.length > 0) {
            fetchTopComment(story.id, story.kids);
          }
        });

        setLoading(false);
        return;
      }

      const feedUrl = getFeedUrl();
      if (!feedUrl) return;

      const storiesResponse = await fetch(feedUrl);
      const storyIds: number[] = await storiesResponse.json();

      const minTime = getTimeFilterSeconds();

      // Fetch more stories to ensure we have enough within time filter
      const storyPromises = storyIds.slice(0, 50).map(async (id) => {
        const response = await fetch(
          `https://hacker-news.firebaseio.com/v0/item/${id}.json`,
        );
        return response.json();
      });

      const fetchedStories: HNStory[] = await Promise.all(storyPromises);

      // Keywords to filter out non-tech content
      const excludeKeywords = [
        // Holidays & Celebrations
        "christmas",
        "xmas",
        "holiday",
        "thanksgiving",
        "halloween",
        "easter",
        "new year",
        "valentine",
        "mother's day",
        "father's day",
        // Politics & Current Events
        "trump",
        "biden",
        "election",
        "political",
        "politics",
        "government",
        "congress",
        "senate",
        "president",
        "democrat",
        "republican",
        // Social/Personal
        "wedding",
        "married",
        "divorce",
        "birthday",
        "funeral",
        "died",
        "passed away",
        "rip ",
        " rip",
        "obituary",
        // Religion
        "church",
        "prayer",
        "bible",
        "religious",
        "god bless",
        // Sports
        "football",
        "basketball",
        "baseball",
        "soccer",
        "nfl",
        "nba",
        // General Non-Tech
        "recipe",
        "cooking",
        "restaurant",
        "travel",
        "vacation",
      ];

      // Filter stories by time, tech relevance, and sort by score
      const filteredStories = fetchedStories.filter((story) => {
        if (!story || story.time < minTime) return false;

        const title = story.title.toLowerCase();
        const url = (story.url || "").toLowerCase();

        // Check if title or URL contains any excluded keywords
        return !excludeKeywords.some(
          (keyword) => title.includes(keyword) || url.includes(keyword),
        );
      });

      if (filteredStories.length > 0) {
        filteredStories.sort((a, b) => b.score - a.score);
        setAllStories(filteredStories);
        const topStories = filteredStories.slice(0, count);
        setStories(topStories);

        // Fetch top comment for each story
        topStories.forEach((story) => {
          if (story.kids && story.kids.length > 0) {
            fetchTopComment(story.id, story.kids);
          }
        });
      } else {
        setAllStories(fetchedStories);
        setStories(fetchedStories.slice(0, count));
      }
    } catch (err) {
      console.error("Error fetching Hacker News:", err);
      setError(true);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  }, [getFeedUrl, getTimeFilterSeconds, count, feedType, savedStories]);

  // Save state to sessionStorage
  useEffect(() => {
    if (stories.length > 0) {
      sessionStorage.setItem(
        "hn_state",
        JSON.stringify({
          stories,
          allStories,
          feedType,
          timeFilter,
          displayCount,
        }),
      );
    }
  }, [stories, allStories, feedType, timeFilter, displayCount]);

  // Restore scroll position
  useEffect(() => {
    const scrollPos = sessionStorage.getItem("hn_scroll");
    if (scrollPos) {
      window.scrollTo(0, parseInt(scrollPos));
      sessionStorage.removeItem("hn_scroll");
    }
  }, []);

  useEffect(() => {
    if (!initialState) {
      setDisplayCount(count);
      fetchStories();
    }
  }, [fetchStories, count, initialState]);

  // Reset display count and fetch stories when feed type or time filter changes
  useEffect(() => {
    if (!isInitialLoad) {
      setDisplayCount(count);
      fetchStories();
    }
  }, [feedType, timeFilter, count, fetchStories, isInitialLoad]);

  const handleLoadMore = async () => {
    setLoadingMore(true);
    const newDisplayCount = displayCount + count;

    // Simulate a small delay for smooth UX
    await new Promise((resolve) => setTimeout(resolve, 300));

    const newStories = allStories.slice(0, newDisplayCount);
    setStories(newStories);
    setDisplayCount(newDisplayCount);

    // Fetch comments for newly displayed stories
    const newlyAddedStories = newStories.slice(displayCount);
    newlyAddedStories.forEach((story) => {
      if (story.kids && story.kids.length > 0 && !comments.has(story.id)) {
        fetchTopComment(story.id, story.kids);
      }
    });

    setLoadingMore(false);
  };

  const handleSaveStory = (storyId: number) => {
    const newSaved = new Set(savedStories);
    if (newSaved.has(storyId)) {
      newSaved.delete(storyId);
    } else {
      newSaved.add(storyId);
    }
    setSavedStories(newSaved);
    localStorage.setItem("hn_saved", JSON.stringify(Array.from(newSaved)));
  };

  const toggleCommentExpansion = (storyId: number) => {
    const newExpanded = new Set(expandedComments);
    if (newExpanded.has(storyId)) {
      newExpanded.delete(storyId);
    } else {
      newExpanded.add(storyId);
    }
    setExpandedComments(newExpanded);
  };

  const stripHtml = (html: string) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const getTimeAgo = (timestamp: number) => {
    const seconds = Math.floor(Date.now() / 1000 - timestamp);
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60,
    };

    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / secondsInUnit);
      if (interval >= 1) {
        return `${interval} ${unit}${interval !== 1 ? "s" : ""} ago`;
      }
    }
    return "just now";
  };

  if (isInitialLoad && loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={30} sx={{ color: "#ff6600" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="h6" color="error">
          Error loading stories. Please try again.
        </Typography>
      </Box>
    );
  }

  const renderStoryCard = (story: HNStory, index: number) => {
    const topComment = comments.get(story.id);
    const isExpanded = expandedComments.has(story.id);
    const isSaved = savedStories.has(story.id);

    return (
      <Card
        key={story.id}
        sx={{
          background:
            "linear-gradient(135deg, rgba(255, 102, 0, 0.08) 0%, rgba(255, 102, 0, 0.03) 100%)",
          border: "1px solid",
          borderColor: isSaved ? "#ff6600" : "rgba(255, 102, 0, 0.2)",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          transition: "transform 0.2s, box-shadow 0.2s",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: 4,
            borderColor: "#ff6600",
          },
        }}
      >
        <CardContent
          sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1.5,
            }}
          >
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                label={`#${index + 1}`}
                size="small"
                sx={{
                  backgroundColor: "#ff6600",
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              />
              {feedType === "ask" && (
                <Chip
                  label="Ask HN"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(0, 150, 136, 0.15)",
                    color: "#009688",
                    fontWeight: 500,
                  }}
                />
              )}
              {feedType === "show" && (
                <Chip
                  label="Show HN"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(33, 150, 243, 0.15)",
                    color: "#2196F3",
                    fontWeight: 500,
                  }}
                />
              )}
            </Box>
            <IconButton
              size="small"
              onClick={() => handleSaveStory(story.id)}
              sx={{ color: isSaved ? "#ff6600" : "text.secondary" }}
            >
              {isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
            </IconButton>
          </Box>

          <Box
            onClick={() => {
              sessionStorage.setItem("hn_scroll", window.scrollY.toString());
              navigate(`/news/${story.id}`);
            }}
            sx={{
              cursor: "pointer",
              flexGrow: 1,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 2,
                lineHeight: 1.4,
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                color: "text.primary",
                "&:hover": {
                  color: "#ff6600",
                },
              }}
            >
              {story.title}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: { xs: 1, sm: 2 },
              flexWrap: "wrap",
              alignItems: "center",
              mb: topComment ? 1 : 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <TrendingUpIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary">
                {story.score} points
              </Typography>
            </Box>

            {story.descendants !== undefined && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CommentIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  {story.descendants}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary">
                {getTimeAgo(story.time)}
              </Typography>
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: { xs: "none", sm: "block" } }}
            >
              by {story.by}
            </Typography>
          </Box>

          {topComment && (
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                onClick={() => toggleCommentExpansion(story.id)}
                endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{
                  textTransform: "none",
                  color: "text.secondary",
                  fontSize: "0.75rem",
                  p: 0,
                  minWidth: "auto",
                }}
              >
                Top comment
              </Button>
              <Collapse in={isExpanded}>
                <Box
                  sx={{
                    mt: 1,
                    p: 1.5,
                    backgroundColor: "rgba(0, 0, 0, 0.05)",
                    borderRadius: 1,
                    borderLeft: "3px solid #ff6600",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mb: 0.5,
                      color: "text.secondary",
                      fontWeight: 600,
                    }}
                  >
                    {topComment.by} • {getTimeAgo(topComment.time)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.85rem",
                      lineHeight: 1.5,
                    }}
                  >
                    {stripHtml(topComment.text).substring(0, 200)}
                    {stripHtml(topComment.text).length > 200 ? "..." : ""}
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      {/* Loading indicator for tab switches */}
      {loading && !isInitialLoad && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              height: "3px",
              backgroundColor: "#ff6600",
              animation: "loadingBar 1s ease-in-out infinite",
              "@keyframes loadingBar": {
                "0%": { width: "0%", marginLeft: "0%" },
                "50%": { width: "50%", marginLeft: "25%" },
                "100%": { width: "0%", marginLeft: "100%" },
              },
            }}
          />
        </Box>
      )}

      {/* Controls */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
        }}
      >
        <Tabs
          value={feedType}
          onChange={(_, value) => {
            setFeedType(value);
            setDisplayCount(count);
          }}
          sx={{
            "& .MuiTab-root": {
              color: "text.secondary",
              "&.Mui-selected": {
                color: "#ff6600",
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#ff6600",
            },
          }}
        >
          <Tab label="Top Stories" value="top" />
          <Tab label="New" value="new" />
          <Tab label="Ask HN" value="ask" />
          <Tab label="Show HN" value="show" />
          <Tab
            label={`Saved${
              savedStories.size > 0 ? ` (${savedStories.size})` : ""
            }`}
            value="saved"
          />
        </Tabs>

        {feedType !== "saved" && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <ButtonGroup size="small" variant="outlined">
              <Button
                onClick={() => setTimeFilter("today")}
                variant={timeFilter === "today" ? "contained" : "outlined"}
                sx={{
                  borderColor: "#ff6600",
                  color: timeFilter === "today" ? "white" : "#ff6600",
                  backgroundColor:
                    timeFilter === "today" ? "#ff6600" : "transparent",
                  "&:hover": {
                    borderColor: "#ff6600",
                    backgroundColor:
                      timeFilter === "today"
                        ? "#ff6600"
                        : "rgba(255,102,0,0.1)",
                  },
                }}
              >
                Today
              </Button>
              <Button
                onClick={() => setTimeFilter("week")}
                variant={timeFilter === "week" ? "contained" : "outlined"}
                sx={{
                  borderColor: "#ff6600",
                  color: timeFilter === "week" ? "white" : "#ff6600",
                  backgroundColor:
                    timeFilter === "week" ? "#ff6600" : "transparent",
                  "&:hover": {
                    borderColor: "#ff6600",
                    backgroundColor:
                      timeFilter === "week" ? "#ff6600" : "rgba(255,102,0,0.1)",
                  },
                }}
              >
                Week
              </Button>
              <Button
                onClick={() => setTimeFilter("month")}
                variant={timeFilter === "month" ? "contained" : "outlined"}
                sx={{
                  borderColor: "#ff6600",
                  color: timeFilter === "month" ? "white" : "#ff6600",
                  backgroundColor:
                    timeFilter === "month" ? "#ff6600" : "transparent",
                  "&:hover": {
                    borderColor: "#ff6600",
                    backgroundColor:
                      timeFilter === "month"
                        ? "#ff6600"
                        : "rgba(255,102,0,0.1)",
                  },
                }}
              >
                Month
              </Button>
            </ButtonGroup>
          </Box>
        )}
      </Box>

      {/* Empty State for Saved Stories */}
      {feedType === "saved" && stories.length === 0 && !loading && (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            px: 2,
          }}
        >
          <BookmarkBorderIcon
            sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
          />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No saved stories yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click the bookmark icon on any story to save it for later
          </Typography>
        </Box>
      )}

      {/* Stories Grid */}
      {stories.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: 3,
          }}
        >
          {stories.flatMap((story, index) => {
            const items = [
              <Box
                key={story.id}
                sx={{
                  animation: "fadeIn 0.5s ease-in",
                  "@keyframes fadeIn": {
                    "0%": { opacity: 0, transform: "translateY(20px)" },
                    "100%": { opacity: 1, transform: "translateY(0)" },
                  },
                }}
              >
                {renderStoryCard(story, index)}
              </Box>,
            ];

            return items;
          })}
        </Box>
      )}

      {/* Load More */}
      {stories.length < allStories.length && (
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Button
            variant="outlined"
            onClick={handleLoadMore}
            disabled={loadingMore}
            sx={{
              borderColor: "#ff6600",
              color: "#ff6600",
              minWidth: 200,
              "&:hover": {
                borderColor: "#ff6600",
                backgroundColor: "rgba(255, 102, 0, 0.1)",
              },
              "&.Mui-disabled": {
                borderColor: "rgba(255, 102, 0, 0.3)",
                color: "rgba(255, 102, 0, 0.5)",
              },
            }}
          >
            {loadingMore ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} sx={{ color: "#ff6600" }} />
                <Typography>Loading...</Typography>
              </Box>
            ) : (
              `Show More Stories (${
                allStories.length - stories.length
              } remaining)`
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default HackerNews;
